<script setup lang="ts">
import { useSessionStore } from "~/stores/session";

const sessionStore = useSessionStore();
const route = useRoute();

const { t } = useI18n();

const isMockMode = useRuntimeConfig().public.MOCK_API_ENABLED !== false;

// Sibling route records (index/new/detail) require prefix matching for active state.
const isExperiencesActive = computed(
  () =>
    route.path === "/app/experiences" ||
    route.path.startsWith("/app/experiences/")
);

const mobileNavOpen = ref(false);

const userName = computed(() =>
  sessionStore.user
    ? `${sessionStore.user.names} ${sessionStore.user.paternalSurname}`.trim()
    : "Usuario"
);

const logout = async () => {
  await sessionStore.logout();
  await navigateTo("/auth/login");
};
</script>

<template>
  <div class="participant-layout">
    <DesignSystemNotificationSection top-size="small" />
    <DesignSystemNavBar :sticky="true">
      <template #burger>
        <button
          class="participant-layout__hamburger"
          type="button"
          :aria-label="t('common.menu')"
          data-testid="participant-nav-toggle"
          @click="mobileNavOpen = !mobileNavOpen"
        >
          <DesignSystemIcon icon="bars" />
        </button>
      </template>
      <template #brand>
        <NuxtLink
          to="/app"
          class="participant-layout__brand"
          data-testid="participant-nav-brand"
        >
          <RelinkLogo />
        </NuxtLink>
      </template>
      <template #start>
        <nav class="participant-layout__nav" :aria-label="t('common.menu')">
          <NuxtLink to="/app">{{ t("common.home") }}</NuxtLink>
          <NuxtLink
            to="/app/experiences"
            :class="{ 'router-link-active': isExperiencesActive }"
            >{{ t("common.experiences") }}</NuxtLink
          >
          <span aria-disabled="true">{{ t("common.skills") }}</span>
        </nav>
      </template>
      <template #end>
        <DesignSystemDropdown
          position="is-bottom-left"
          aria-role="menu"
          :mobile-modal="false"
        >
          <template #trigger="{ icon }">
            <button class="participant-layout__user" type="button">
              {{ userName }}
              <DesignSystemIcon :icon="icon" size="is-small" />
            </button>
          </template>
          <DesignSystemDropdownItem aria-role="menuitem" @click="logout">
            {{ t("common.logout") }}
          </DesignSystemDropdownItem>
        </DesignSystemDropdown>
      </template>
    </DesignSystemNavBar>
    <DesignSystemSidebar
      v-model="mobileNavOpen"
      mobile-only
      class="participant-layout__mobile-nav"
    >
      <nav :aria-label="t('common.menu')">
        <NuxtLink to="/app" data-testid="participant-mobile-nav-home">{{
          t("common.home")
        }}</NuxtLink>
        <NuxtLink
          to="/app/experiences"
          data-testid="participant-mobile-nav-experiences"
          :class="{ 'router-link-active': isExperiencesActive }"
          >{{ t("common.experiences") }}</NuxtLink
        >
        <span aria-disabled="true">{{ t("common.skills") }}</span>
      </nav>
    </DesignSystemSidebar>
    <slot />
    <span v-if="isMockMode" class="mock-badge" role="status">
      {{ t("common.mockModeBanner") }}
    </span>
  </div>
</template>

<style scoped lang="scss">
@use "bulma/sass/utilities/initial-variables" as bulma;

.participant-layout {
  min-height: 100dvh;
  background: $white;
}

.participant-layout__brand {
  display: flex;

  img {
    width: auto;
    height: $spacing_6;
  }
}

.participant-layout__nav {
  display: flex;
  gap: 16px;
}

.participant-layout__nav a,
.participant-layout__nav span,
.participant-layout__mobile-nav a,
.participant-layout__mobile-nav span {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  border-radius: 8px;
  color: $tertiary;
  font-size: 16px;
  line-height: 24px;
  padding: 8px 20px;
  text-decoration: none;
}

.participant-layout__nav span[aria-disabled="true"],
.participant-layout__mobile-nav span[aria-disabled="true"] {
  color: $grey-light;
  cursor: not-allowed;
}

.participant-layout__nav a.router-link-active,
.participant-layout__mobile-nav a.router-link-active {
  background: $tertiary-light;
}

.participant-layout__hamburger {
  display: flex;
  align-items: center;
  border: 0;
  background: none;
  color: $tertiary;
  font-size: $size-medium;
  cursor: pointer;
}

.participant-layout__mobile-nav {
  :deep(.sidebar-content) {
    @include ds-stack($spacing_2);
    background: $white;
    padding: $spacing_4 $spacing_3;
  }

  nav {
    @include ds-stack($spacing_2);
  }
}

.participant-layout__user {
  display: inline-flex;
  min-height: 40px;
  white-space: nowrap;
  align-items: center;
  border: 0;
  background: $white;
  color: $grey;
  cursor: pointer;
  font-size: 16px;
  gap: 8px;
  justify-content: center;
  line-height: 24px;
  padding: 8px 12px;
}

// Matches DesignSystemNavBar and DesignSystemSidebar desktop breakpoint ($tablet).
@media (width < #{bulma.$tablet}) {
  .participant-layout__nav {
    display: none;
  }
}

.mock-badge {
  position: fixed;
  right: $spacing_2;
  bottom: $spacing_2;
  // Below modal backdrops (z-index 100)
  z-index: 10;
  padding: 2px $spacing_2;
  border-radius: 4px;
  background-color: $warning;
  color: $black-ter;
  font-size: $size-small;
  opacity: 0.75;
  pointer-events: none;
}
</style>
